(() => {
  const channel = 'coach-creative-v1';
  if (customElements.get('coach-customizer-embed')) return;
  customElements.define('coach-customizer-embed', class extends HTMLElement {
    connectedCallback() {
      if (this.listener) return;
      this.dialog = this.querySelector('dialog');
      this.frame = this.querySelector('iframe');
      this.status = this.querySelector('[data-status]');
      this.button = this.querySelector('[data-open]');
      this.completed = new Set();
      try {
        this.product = JSON.parse(this.querySelector('[data-product]').textContent);
        this.currency = JSON.parse(this.querySelector('[data-currency]').textContent);
        this.defaultVariant = JSON.parse(this.querySelector('[data-selected]').textContent);
        const url = new URL(this.dataset.engineUrl);
        if (url.protocol !== 'https:' || url.username || url.password) throw Error('Use an HTTPS engine URL without credentials.');
        this.engineOrigin = url.origin;
        url.searchParams.set('parentOrigin', location.origin);
        this.engineUrl = url.href;
        this.openListener = () => this.open();
        this.closeListener = () => this.dialog.close();
        this.listener = event => this.receive(event);
        this.button.addEventListener('click', this.openListener);
        this.querySelector('[data-close]').addEventListener('click', this.closeListener);
        window.addEventListener('message', this.listener);
        if (this.dataset.inline === 'true') this.open();
      } catch (error) {
        this.button.disabled = true;
        this.button.textContent = 'Customizer unavailable';
        console.error('Coach Creative setup:', error.message);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('message', this.listener);
      this.button?.removeEventListener('click', this.openListener);
      this.querySelector('[data-close]')?.removeEventListener('click', this.closeListener);
      clearTimeout(this.timer);
      this.listener = null;
    }
    open() {
      if (this.dataset.inline !== 'true') this.dialog.showModal();
      if (!this.loaded) {
        this.status.textContent = 'Loading your customizer…';
        this.timer = setTimeout(() => {
          this.status.textContent = 'The customizer could not connect. Close and reopen it to retry, or contact the store.';
          this.loaded = false;
        }, 30000);
        this.frame.src = this.engineUrl;
        this.loaded = true;
      } else if (this.ready) this.sendContext();
    }
    selectedVariant() {
      const form = this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null;
      const candidate = form?.querySelector('[name="id"]')?.value || new URL(location.href).searchParams.get('variant') || this.defaultVariant;
      return this.product.variants.some(v => String(v.id) === String(candidate)) ? String(candidate) : String(this.defaultVariant);
    }
    send(data) { this.frame.contentWindow.postMessage({ ...data, channel }, this.engineOrigin); }
    sendContext() {
      if (this.dataset.mode === 'picker' && !this.product) {
        this.send({ type: 'picker-context', currency: this.currency });
        return;
      }
      this.send({ type: 'context', product: this.product, currency: this.currency, selectedVariantId: this.selectedVariant() });
    }
    async resolveProduct(data) {
      if (!/^[A-Za-z0-9_-]{1,60}$/.test(data.style) || !Array.isArray(data.upcs) || !data.upcs.length || data.upcs.length > 250 || !data.upcs.every(upc => typeof upc === 'string' && /^\d{6,20}$/.test(upc))) throw Error('Invalid bag selection.');
      const root = this.dataset.cartRoot.replace(/\/?$/, '/');
      if (!root.startsWith('/') || root.startsWith('//')) throw Error('Invalid storefront path.');
      const upcs = new Set(data.upcs);
      const query = new URLSearchParams({ q: data.style, 'resources[type]': 'product', 'resources[limit]': '10' });
      const response = await fetch(root + 'search/suggest.json?' + query, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw Error('Shopify product lookup failed. Please try again.');
      const results = (await response.json()).resources?.results?.products || [];
      const matches = [];
      for (const candidate of results) {
        if (!/^[a-zA-Z0-9_-]+$/.test(candidate.handle)) continue;
        const details = await fetch(root + 'products/' + candidate.handle + '.js', { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
        if (!details.ok) continue;
        const product = await details.json();
        const variants = product.variants?.filter(variant => upcs.has(String(variant.sku)) || upcs.has(String(variant.barcode)));
        if (variants?.length) matches.push({ ...product, variants });
      }
      if (matches.length !== 1) throw Error('This bag could not be matched uniquely to Shopify by UPC. Please contact the store.');
      const product = matches[0];
      const first = product.variants.find(variant => variant.available);
      if (!first) throw Error('This bag is currently unavailable in Shopify.');
      this.product = product;
      this.defaultVariant = first.id;
      this.sendContext();
    }
    async receive(event) {
      if (event.origin !== this.engineOrigin || event.source !== this.frame.contentWindow || event.data?.channel !== channel) return;
      const data = event.data;
      if (data.type === 'show-picker' && this.dataset.mode === 'picker' && !this.busy) { this.product = null; return; }
      if (data.type === 'select-product' && this.dataset.mode === 'picker') {
        if (!this.ready || !this.dialog.open || this.resolving || this.busy) return;
        this.resolving = true;
        try { await this.resolveProduct(data); }
        catch (error) { this.send({ type: 'product-error', error: error.message }); }
        finally { this.resolving = false; }
        return;
      }
      if (data.type === 'ready') {
        clearTimeout(this.timer);
        this.ready = true;
        this.status.textContent = '';
        this.sendContext();
        return;
      }
      if (data.type !== 'add-to-cart' || typeof data.requestId !== 'string' || !data.requestId || data.requestId.length > 100) return;
      if (this.completed.has(data.requestId)) {
        this.send({ type: 'cart-result', requestId: data.requestId, ok: true });
        return;
      }
      if (this.busy) return;
      this.busy = true;
      try {
        if (!this.dialog.open || !this.ready) throw Error('Open the customizer before adding a design.');
        if (!Array.isArray(data.items) || !data.items.length || data.items.length > 25) throw Error('Invalid design.');
        if (!this.product?.variants.some(v => String(v.id) === String(data.items[0].id) && v.available)) throw Error('Please select an available bag variant.');
        const items = data.items.map(item => {
          if (!/^[1-9]\d*$/.test(String(item.id)) || item.quantity !== 1 || !item.properties || typeof item.properties !== 'object' || Array.isArray(item.properties)) throw Error('Invalid cart item.');
          const entries = Object.entries(item.properties);
          if (entries.length > 40) throw Error('Too many design properties.');
          for (const [key, value] of entries) {
            if (!key || key.length > 80 || typeof value !== 'string' || value.length > 250 || ['__proto__', 'constructor', 'prototype'].includes(key)) throw Error('Invalid design property.');
          }
          return { id: String(item.id), quantity: 1, properties: Object.fromEntries(entries) };
        });
        const root = this.dataset.cartRoot.replace(/\/?$/, '/');
        if (!root.startsWith('/') || root.startsWith('//')) throw Error('Invalid Shopify cart path.');
        this.status.textContent = 'Adding your design to the cart…';
        // A lost response can follow a successful add. Never retry automatically.
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw Error(result.description || 'Shopify could not add this design. Check your cart before retrying.');
        this.completed.add(data.requestId);
        this.send({ type: 'cart-result', requestId: data.requestId, ok: true });
        this.dispatchEvent(new CustomEvent('coach:cart-added', { bubbles: true, detail: result }));
        location.assign(root + 'cart');
      } catch (error) {
        const message = error.message || 'Unable to add your design. Check your cart before retrying.';
        this.status.textContent = message;
        this.send({ type: 'cart-result', requestId: data.requestId, ok: false, error: message });
      } finally { this.busy = false; }
    }
  });
})();
